"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import React from "react";

const IntroData = [
  {
    id: 1,
    imgsrc: "/assets/bal-kand.png",
    title: "🏹 Bala Kanda - The Beginning",
    paragraph:
      "Bala Kanda narrates the auspicious birth of Rama in Ayodhya after King Dasharatha performs a sacred yajna seeking sons. It portrays Rama's disciplined childhood, his training in dharma, archery, and scriptures under sage Vishwamitra, and his early acts of compassion and valor. The redemption of Ahalya reflects Rama's divine grace, while Sita's swayamvar highlights his unmatched strength as he breaks Shiva's bow. This kanda sets the foundation of the Ramayan, presenting Rama as the embodiment of virtue, courage, and divine purpose. It emphasizes how destiny and dharma guide his journey, establishing the moral framework for the epic's unfolding story.",
  },
  {
    id: 2,
    imgsrc: "/assets/ayodhya--kand.png",
    title: "👣 Ayodhya Kanda - The Exile Begins",
    paragraph:
      "Ayodhya Kanda unfolds the palace drama where Queen Kaikeyi, swayed by Manthara, demands Rama's exile and Bharata's coronation. Rama accepts with serenity, embodying dharma, while Sita and Lakshmana accompany him in devotion and sacrifice. Bharata, though offered the throne, refuses to rule and instead places Rama's sandals on Ayodhya's throne, symbolizing loyalty and humility. This kanda highlights the pain of separation, the strength of familial bonds, and moral dilemmas that test righteousness. It emphasizes the clash between personal desires and duty, making it one of the epic's most poignant sections.",
  },
  {
    id: 3,
    imgsrc: "/assets/aranaya--kand.png",
    title: "🦅 Aranya Kanda - Trials in the Forest",
    paragraph:
      "Aranya Kanda depicts the forest life of Rama, Sita, and Lakshmana in Panchavati, where they embrace simplicity and spiritual living. Their exile is disrupted by Shurpanakha's advances, sparking conflict with Ravana's kin. Maricha, disguised as a golden deer, lures Rama away, allowing Ravana to abduct Sita. Jatayu, the noble bird, sacrifices his life in loyalty and courage. This kanda explores temptation, deception, and the consequences of desire, showing how one act can change destiny. It deepens the epic by highlighting life's fragility and the trials faced in pursuit of dharma",
  },
  {
    id: 4,
    imgsrc: "/assets/kiskindha--kand.png",
    title: "🤝 Kishkindha Kanda - Alliance with Sugriva",
    paragraph:
      "Kishkindha Kanda recounts Rama's alliance with Sugriva, the exiled Vanara king, whom he helps defeat Vali to reclaim his throne. In gratitude, Sugriva pledges support in the search for Sita. Hanuman emerges as Rama's most devoted servant, embodying strength, humility, and loyalty. This kanda highlights the importance of alliances, trust, and friendship in overcoming adversity, while showcasing the unity of humans and Vanaras in pursuit of justice. It sets the stage for Sita's search, emphasizing cooperation, strategy, and faith in achieving seemingly impossible goals.",
  },
  {
    id: 5,
    imgsrc: "/assets/sundara-kand.jpg",
    title: "💨 Sundara Kanda - Hanuman's Journey",
    paragraph:
      "Sundara Kanda, the most celebrated part of the Ramayan, recounts Hanuman's leap across the ocean to Lanka. He finds Sita in Ashok Vatika, consoles her with Rama's message, and displays unmatched strength by burning parts of Lanka before returning. His journey symbolizes courage, devotion, and faith overcoming obstacles. Often recited for strength and hope, this kanda serves as a spiritual guide for millions. Its focus on perseverance, loyalty, and divine grace makes it the heart of the epic.",
  },
  {
    id: 6,
    imgsrc: "/assets/yuddha-kand.jpg",
    title: "🗡️ Yuddha Kanda - The Great War",
    paragraph:
      "Yuddha Kanda narrates the epic battle between Rama's Vanara army and Ravana's forces. The Vanaras build a bridge to Lanka, symbolizing determination and teamwork. Fierce clashes follow: Indrajit, Kumbhakarna, and other warriors fall before Rama finally slays Ravana, restoring dharma. Sita is rescued, though her purity is tested, reflecting societal challenges. This kanda embodies the triumph of good over evil, the fulfillment of vows, and the victory of righteousness. It also reveals the complexities of war, sacrifices made, and the emotional toll, reminding that even victory carries loss.",
  },
  {
    id: 7,
    imgsrc: "/assets/uttara-kand.jpg",
    title: "📜 Uttara Kanda - Aftermath and Ram Rajya",
    paragraph:
      "Uttara Kanda portrays Rama's reign in Ayodhya, celebrated as Ram Rajya, where justice, prosperity, and peace prevail. Yet doubts about Sita's purity force her into exile at Valmiki's ashram, where she gives birth to Lava and Kusha. Their confrontation with Rama's army and eventual reunion with their father adds emotional depth. The epic concludes with Rama's departure from the mortal world, symbolizing the eternal legacy of dharma, sacrifice, and ideal governance. This kanda highlights the challenges of leadership, the burden of public opinion, and the sacrifices demanded by dharma, affirming that true greatness lies in upholding righteousness.",
  },
];

const bgStyles = {
  1: "bg-[#fdf6e3]",
  2: "bg-[#fdf6e3]",
  3: "bg-gradient-to-r from-green-100 to-green-200 text-green-900",
  4: "bg-[#fdf6e3]",
  5: "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-900",
  6: "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-900",
  7: "bg-[#fdf6e3]",
};

const motionConfigs = {
  1: {
    initial: { opacity: 0, y: 100 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7 },
  },
  2: {
    initial: { opacity: 0, x: -50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.7 },
  },
  3: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { duration: 0.7 },
  },
  4: {
    initial: { rotate: -10, opacity: 0 },
    animate: { rotate: 0, opacity: 1 },
    transition: { duration: 0.7 },
  },
  5: {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.7 },
  },
  6: {
    initial: { opacity: 0, y: -50 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay: 0.3 },
  },
  7: {
    initial: { scale: 0.5, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { duration: 0.9, delay: 0.3 },
  },
};

const MotionImage = motion(Image);

const Intro = () => {
  return (
    <section className="bg-[#f7f3e9] py-12 px-5 md:px-12 mx-auto flex flex-col gap-10 font-[Inter] text-[#222222]">
      {/* Intro Section */}
      <div className="flex flex-col gap-4 justify-center items-center text-center">
        <MotionImage
          src="/assets/raamIntroImage.jpg"
          alt="shri-ram-image"
          width={600}
          height={400}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
          className="object-cover w-[90vw] md:w-[70vw] h-[50vh] md:h-[70vh] mx-auto rounded-2xl shadow-lg border-4 border-[#d4af37]"
        />
        <h2 className=" text-2xl md:text-4xl font-[Cinzel] text-[#1e3a8a] font-bold select-none">
          Introduction to Ramayana
        </h2>
        <p className="md:text-xl text-sm max-w-3xl leading-snug  select-none tracking-tight md:tracking-[1px]">
          The Ramayan is one of the most loved epics of India. Written by sage
          Valmiki, it tells the story of Lord Rama, his wife Sita, and his
          brother Lakshmana. The epic has seven parts and about 24,000 verses.
          It begins with Rama's birth in Ayodhya, his marriage to Sita, and
          later his exile to the forest. The story follows Sita's capture by
          Ravana, the king of Lanka, and Rama's journey with Hanuman and the
          Vanara army to rescue her. In the end, Rama defeats Ravana and returns
          to Ayodhya to rule with justice and kindness. The Ramayan teaches
          lessons of duty, love, sacrifice, and the victory of good over evil.
        </p>
      </div>

      {/* Timeline Cards */}
      <div
        id="chronicle"
        className="flex flex-col md:gap-12 gap-8 justify-center mx-auto w-full "
      >
        <div className="max-w-3xl items-center justify-center mx-auto text-2xl md:text-4xl font-[Cinzel] text-[#1e3a8a] font-bold select-none">
          Epic Saga: The Seven Kandas
        </div>

        {IntroData.map(({ id, imgsrc, title, paragraph }) => (
          <div
            key={id}
            className={`${bgStyles[id]} rounded-xl shadow-lg flex flex-col md:flex-row gap-x-6 justify-center items-center pb-3.5`}
          >
            <motion.div
              initial={motionConfigs[id].initial}
              whileInView={motionConfigs[id].animate}
              transition={motionConfigs[id].transition}
              viewport={{ once: true, amount: 0.57 }}
              className="flex justify-center items-center h-full "
            >
              <Image
                src={imgsrc}
                alt={title.split("-")[0].trim()}
                width={400}
                height={500}
                loading="lazy"
                className="object-cover w-[80vw] h-full rounded-xl flex-1 md:w-[47vw] md:h-[60vh] "
              />
            </motion.div>
            <div className="flex flex-1 flex-col gap-2  md:gap-3 justify-start items-center p-3">
              <h2 className="text-lg md:text-2xl font-[Cinzel] text-[#1e3a8a] font-semibold select-none">
                {title}
              </h2>
              <p className="text-sm md:text-lg font-[Inter] max-w-md leading-snug md:leading-snug tracking-[1px] select-none">
                {paragraph}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Intro;
